import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Rocket,
  CalendarDays,
  Check,
  Loader2,
  TrendingUp,
  Eye,
  Star,
  ArrowRight,
  Clock,
  Sparkles
} from 'lucide-react';
import { fr } from 'date-fns/locale';
import { differenceInCalendarDays, addDays, format, isBefore, startOfDay } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({ baseURL: API_URL });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const Boost = () => {
  const [boostStatus, setBoostStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const { user } = useAuth();

  const PRICE_PER_DAY = 5;
  const COMING_SOON = true;

  useEffect(() => {
    fetchBoostStatus();
    // Check URL for success/cancel
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true' && params.get('session_id')) {
      verifyPayment(params.get('session_id'));
    }
  }, []);

  const fetchBoostStatus = async () => {
    try {
      const res = await api.get('/api/boost/status');
      setBoostStatus(res.data);
    } catch (err) {
      console.error('Error fetching boost status:', err);
    } finally {
      setLoading(false);
    }
  };

  const verifyPayment = async (sessionId) => {
    try {
      const res = await api.post('/api/boost/verify', { session_id: sessionId });
      if (res.data.status === 'active') {
        toast.success('Mise en avant activée !', {
          description: `Votre restaurant est mis en avant jusqu'au ${format(new Date(res.data.end_date), 'dd MMMM yyyy', { locale: fr })}`
        });
        fetchBoostStatus();
        // Clean URL
        window.history.replaceState({}, '', '/boost');
      }
    } catch (err) {
      toast.error('Erreur lors de la vérification du paiement');
    }
  };

  const selectedDays = dateRange.from && dateRange.to
    ? differenceInCalendarDays(dateRange.to, dateRange.from) + 1
    : 0;
  const totalPrice = selectedDays * PRICE_PER_DAY;

  const handleBoost = async () => {
    if (selectedDays < 1) {
      toast.error('Veuillez sélectionner une période');
      return;
    }
    setProcessing(true);
    try {
      const res = await api.post('/api/boost/checkout', {
        days: selectedDays,
        origin_url: window.location.origin
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la création du paiement');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const activeBoost = boostStatus?.active_boost;
  const pastBoosts = boostStatus?.past_boosts || [];
  const today = startOfDay(new Date());

  return (
    <div className="space-y-6 relative">
      {/* Coming Soon Overlay - within page, not fixed */}
      {COMING_SOON && (
        <div className="absolute inset-0 z-50 bg-white/80 backdrop-blur-sm flex items-center justify-center rounded-2xl" data-testid="boost-coming-soon">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm mx-4 text-center border">
            <div className="h-16 w-16 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
              <Rocket className="h-8 w-8 text-orange-500" />
            </div>
            <h2 className="font-heading text-xl font-bold text-gray-900 mb-2">Option à venir</h2>
            <p className="text-gray-500 text-sm">
              La mise en avant de votre restaurant sera bientôt disponible. Restez connecté !
            </p>
          </div>
        </div>
      )}
      {/* Header */}
      <div>
        <h1 className="text-2xl font-heading font-bold text-gray-900 flex items-center gap-2">
          <Rocket className="h-6 w-6 text-orange-500" />
          Mise en avant
        </h1>
        <p className="text-gray-500 mt-1">Boostez la visibilité de votre restaurant sur la plateforme</p>
      </div>

      {/* Active boost */}
      {activeBoost && (
        <Card className="border-2 border-orange-400 bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-xl bg-orange-500 flex items-center justify-center flex-shrink-0">
                <Star className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-gray-900">Boost actif</h3>
                  <Badge className="bg-orange-500 text-white">En cours</Badge>
                </div>
                <p className="text-sm text-gray-600">
                  Votre restaurant est mis en avant jusqu'au{' '}
                  <span className="font-semibold">{format(new Date(activeBoost.end_date), 'dd MMMM yyyy', { locale: fr })}</span>
                </p>
                <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <CalendarDays className="h-4 w-4" />
                    {activeBoost.days} jour{activeBoost.days > 1 ? 's' : ''}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {activeBoost.amount}€ payé
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Calendar */}
        <Card className="rounded-2xl border border-gray-200" data-testid="boost-calendar-card">
          <CardContent className="p-6">
            <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-orange-500" />
              Choisissez la période
            </h3>
            <p className="text-sm text-gray-500 mb-4">Sélectionnez les dates de début et de fin</p>
            
            <Calendar
              mode="range"
              selected={dateRange}
              onSelect={(range) => setDateRange(range || { from: null, to: null })}
              locale={fr}
              disabled={(date) => isBefore(date, today)}
              numberOfMonths={1}
              className="rounded-xl border border-gray-100 p-3"
              data-testid="boost-calendar"
            />

            {selectedDays > 0 && (
              <div className="mt-4 p-4 bg-gray-50 rounded-xl space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Période</span>
                  <span className="font-medium text-gray-900">
                    {format(dateRange.from, 'dd MMM', { locale: fr })} - {format(dateRange.to, 'dd MMM yyyy', { locale: fr })}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Durée</span>
                  <span className="font-medium text-gray-900">{selectedDays} jour{selectedDays > 1 ? 's' : ''}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Prix par jour</span>
                  <span className="font-medium text-gray-900">{PRICE_PER_DAY}€</span>
                </div>
                <div className="border-t border-gray-200 pt-2 flex justify-between">
                  <span className="font-semibold text-gray-900">Total</span>
                  <span className="font-bold text-xl text-orange-500">{totalPrice}€</span>
                </div>
              </div>
            )}

            <Button
              onClick={handleBoost}
              disabled={selectedDays < 1 || processing}
              className="w-full mt-4 h-12 bg-gray-900 hover:bg-gray-800 text-white rounded-xl font-medium text-base"
              data-testid="boost-pay-btn"
            >
              {processing ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  Payer {totalPrice > 0 ? `${totalPrice}€` : ''} et mettre en avant
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Benefits + info */}
        <div className="space-y-5">
          <Card className="rounded-2xl border border-gray-200">
            <CardContent className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-orange-500" />
                Avantages de la mise en avant
              </h3>
              <div className="space-y-4">
                {[
                  { icon: <Eye className="h-5 w-5" />, title: "Visibilité maximale", desc: "Votre restaurant apparaît en premier dans la liste Explorer" },
                  { icon: <Star className="h-5 w-5" />, title: "Badge \"En vedette\"", desc: "Un badge distinctif attire l'attention des utilisateurs" },
                  { icon: <TrendingUp className="h-5 w-5" />, title: "Plus de clients", desc: "Augmentez votre trafic et vos commandes" }
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0 text-orange-500">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="font-medium text-sm text-gray-900">{item.title}</h4>
                      <p className="text-xs text-gray-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border border-gray-200 bg-gray-50">
            <CardContent className="p-5">
              <h4 className="font-medium text-sm text-gray-900 mb-2">Tarification</h4>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-3xl font-bold text-gray-900">{PRICE_PER_DAY}€</span>
                <span className="text-gray-500 text-sm">/ jour</span>
              </div>
              <ul className="space-y-1.5 text-sm text-gray-600">
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> Paiement sécurisé par Stripe</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> Activation immédiate</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> Choisissez librement la durée</li>
              </ul>
            </CardContent>
          </Card>

          {/* Past boosts */}
          {pastBoosts.length > 0 && (
            <Card className="rounded-2xl border border-gray-200">
              <CardContent className="p-5">
                <h4 className="font-medium text-sm text-gray-900 mb-3">Historique</h4>
                <div className="space-y-2">
                  {pastBoosts.map((b, i) => (
                    <div key={i} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded-lg">
                      <span className="text-gray-600">{b.days}j - {b.amount}€</span>
                      <span className="text-gray-400 text-xs">
                        Terminé le {format(new Date(b.end_date), 'dd/MM/yyyy')}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Boost;
